import type { IAppStorage } from '@aesop-fables/scrinium';
import { ScriniumServices } from '@aesop-fables/scrinium';
import { inject, createServiceModule, Scopes } from '@aesop-fables/containr';
import { BehaviorSubject } from 'rxjs';
import { Disposable } from '../../hooks/useConstant';
import { TemplateData } from '../../models/SignUpData';
import {
  OffboardingStepData,
  OffboardingStepsDefault,
  OffboardingStepsAdp,
} from '../../models/OffboardingData';
import { OffboardingCompartments, offboardingStorageKey } from '../../data/offboarding';
import { Location } from 'react-router';

export const offboardingWizardKey = 'services/offboardingWizard';

export enum OffboardingTemplateIdEnum {
  'MemberPays' = 1,
  'OrgPays' = 5,
  'Adp' = 6,
}

const getOffboardingSteps = (template: number): OffboardingStepData[] => {
  switch (template) {
    case OffboardingTemplateIdEnum.OrgPays:
      return OffboardingStepsDefault;
    case OffboardingTemplateIdEnum.MemberPays:
      return OffboardingStepsDefault;
    case OffboardingTemplateIdEnum.Adp:
      return OffboardingStepsAdp;
    default:
      return OffboardingStepsDefault;
  }
};

class OffboardingWizard implements Disposable {
  private readonly offboardingComplete: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private offboardingSteps: OffboardingStepData[] = OffboardingStepsDefault;

  constructor(
    @inject(ScriniumServices.AppStorage)
    private readonly appStorage: IAppStorage,
  ) {
    this.init();
  }

  async init() {
    try {
      const offboardingDataCache =
        this.appStorage.retrieve<OffboardingCompartments>(offboardingStorageKey);
      offboardingDataCache.observe$<TemplateData>('offboardingTemplate').subscribe(template => {
        if (template) {
          this.offboardingSteps = getOffboardingSteps(template.id);
        }
      });
    } catch (err) {
      console.error(err);
    }
  }

  get offboardingComplete$(): BehaviorSubject<boolean> {
    return this.offboardingComplete;
  }

  offboardingCompleted() {
    this.offboardingComplete.next(true);
  }

  resumeOffboarding(lastCompletedStep: string): OffboardingStepData {
    const index = this.offboardingSteps.findIndex(s => s.current === lastCompletedStep);
    const step = this.offboardingSteps.at(index + 1);
    return (
      step ??
      this.offboardingSteps.at(0) ?? {
        current: '/',
        route: '/uploadLogo',
        totalSteps: 3,
        activeStep: 0,
      }
    );
  }

  nextStep(location: Location): OffboardingStepData {
    const current = location.pathname;
    const index = this.offboardingSteps.findIndex(s => s.current === current);
    const step = this.offboardingSteps.at(index + 1);
    return (
      step ??
      this.offboardingSteps.at(0) ?? {
        current: '/',
        route: '/uploadLogo',
        totalSteps: 3,
        activeStep: 0,
      }
    );
  }

  getStepInfo(current: string): { activeStep: number; totalSteps: number } {
    const currentStep = this.offboardingSteps.find(step => step.current === current) ?? {
      totalSteps: 0,
      activeStep: 0,
    };
    const totalSteps = currentStep?.totalSteps;
    const activeStep = currentStep?.activeStep;
    return { activeStep: activeStep, totalSteps: totalSteps };
  }

  dispose(): void {
    console.log('disposing OffboardingWizard');
  }
}

export const withOffboardingWizard = createServiceModule(offboardingWizardKey, services => {
  services.autoResolve<OffboardingWizard>(
    offboardingWizardKey,
    OffboardingWizard,
    Scopes.Singleton,
  );
});

export { OffboardingWizard };
